import { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { CloseIcon, PlusIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import InputSelect from 'UiKit/input-select';
import InputDatepicker from 'UiKit/input-datepicker';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';

import styles from './styles.module.css';

const OnLeaveHosts = ({ isAnyHostLeave, setValue, rulesetDetail, hostLeaves, maxDate, minDate, customDoubleDates, peakDays }) => {
  const { t } = useTranslation('schedule-generate');

  const [excludeDates, setExcludeDates] = useState([]);
  const hostsOption = rulesetDetail?.users?.map((user) => {
    return {
      ...user,
      label: user.name,
      value: user.id,
    };
  });

  const handleAddHost = () => {
    const newHostLeaves = [
      ...hostLeaves,
      {
        hostId: '',
        leaves: [],
      },
    ];
    setValue('hostLeaves', newHostLeaves);
  };

  const handleChangeHost = (label, value, index) => {
    const newHostLeaves = [...hostLeaves];
    newHostLeaves[index] = {
      ...newHostLeaves[index],
      [label]: value,
    };
    setValue('hostLeaves', newHostLeaves);
  };

  const handleDeleteHost = (index) => {
    if (hostLeaves.length === 1) {
      setValue('isAnyHostLeave', false);
    }
    setValue(
      'hostLeaves',
      hostLeaves.filter((_, idx) => {
        return index !== idx;
      })
    );
  };

  const handleCheckIsAnyHost = (event) => {
    const { checked } = event.target;
    setValue('isAnyHostLeave', checked);
    if (checked) {
      setValue('hostLeaves', [
        {
          hostId: '',
          leaves: [],
        },
      ]);
    } else {
      setValue('hostLeaves', []);
    }
  };

  const startDoubleDates = useMemo(() => new Date(customDoubleDates[0]), [customDoubleDates]);
  const endDoubleDates = useMemo(() => new Date(customDoubleDates[1]), [customDoubleDates]);
  // const startPeakDays = useMemo(() => new Date(peakDays[0]), [peakDays]);
  // const endPeakDays = useMemo(() => new Date(peakDays[1]), [peakDays]);

  useEffect(() => {
    let tempArray = [];
    const month = startDoubleDates.getMonth();
    const year = startDoubleDates.getFullYear();

    if (customDoubleDates) {
      for (let i = startDoubleDates.getDate(); i <= endDoubleDates.getDate(); i++) {
        tempArray.push(new Date(year, month, i));
      }
    }

    if (peakDays) {
      for (const peakDay of peakDays) {
        const [startDate, endDate] = peakDay;
        if (startDate && endDate) {
          const month = startDate.getMonth();
          const year = startDate.getFullYear();

          for (let i = startDate.getDate(); i <= endDate.getDate(); i++) {
            tempArray.push(new Date(year, month, i));
          }
        }
      }
    }

    setExcludeDates(tempArray);
  }, [startDoubleDates, endDoubleDates, peakDays, customDoubleDates]);

  return (
    <Box>
      <Checkbox checked={isAnyHostLeave} isChecked={isAnyHostLeave} onChange={handleCheckIsAnyHost}>
        <TextRegular>{t('hostOnLeave')}</TextRegular>
      </Checkbox>
      <Condition statement={isAnyHostLeave}>
        <Box className={styles['hosts__container']}>
          <Flex marginBottom={'6px'}>
            <Box width={'60%'}>
              <TextBold>{t('hostRequestsLeave')}</TextBold>
            </Box>
            <Box width={'40%'}>
              <TextBold>{t('leaveDate')}</TextBold>
            </Box>
          </Flex>
          <Iteration
            items={hostLeaves}
            render={(host, index) => {
              const hostValue = hostLeaves[index].hostId;
              const selectedDates = hostLeaves[index]?.leaves;
              return (
                <Flex key={index} gap={'8px'} marginBottom={'6px'}>
                  <Flex width={'60%'} gap={'8px'} alignItems={'center'}>
                    <Box width={'95%'}>
                      <InputSelect
                        options={hostsOption || []}
                        minHeight="34px"
                        placeholder={t('fieldSelectHostLeavePlaceholder')}
                        onChange={(value) => handleChangeHost('hostId', value, index)}
                        value={hostValue}
                      />
                    </Box>
                    <Flex width={'5%'} justifyContent={'center'}>
                      <TextBold>-</TextBold>
                    </Flex>
                  </Flex>
                  <Flex width={'40%'} alignItems={'center'}>
                    <Box width={'90%'}>
                      <InputDatepicker
                        datepickerClassname={styles['hosts__container--datepicker']}
                        selectsMultiple={true}
                        selectedDates={selectedDates}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat="dd MMM"
                        excludeDates={excludeDates}
                        shouldCloseOnSelect={false}
                        placeholder="DD MMM"
                        className={'form__wrapper--datepicker'}
                        onChange={(value) => {
                          handleChangeHost('leaves', value, index);
                        }}
                      />
                    </Box>
                    <Box width={'10%'}>
                      <CloseIcon color="var(--color-primary60)" size="12px" className={styles['hosts__icon--delete']} onClick={() => handleDeleteHost(index)} />
                    </Box>
                  </Flex>
                </Flex>
              );
            }}
          />
          <Box className={styles['hosts__container--plus']} onClick={handleAddHost}>
            <PlusIcon color="var(--color-primary60)" size="12px" className={styles['hosts__icon--plus']} />
            <TextSemiBold className={styles['hosts__text--plus']}>{t('addHost')}</TextSemiBold>
          </Box>
        </Box>
      </Condition>
    </Box>
  );
};

export default OnLeaveHosts;
